import {performanceMetricsVariants} from "@buildwithflux/core";
import {APP_VERSION} from "@buildwithflux/shared";
import {tag} from "@palette.dev/browser";
import {useEffect} from "react";
import {z} from "zod";

import {useFluxServices} from "injection/hooks";

const PALETTE_ENABLED_KEY = "flux_performance_metrics_enabled";
const DEFAULT_VALUE = "disabled";

const PerformanceMetricsEnum = z.enum(performanceMetricsVariants);

/**
 * List of performance-relevant feature flags to tag
 * These flags have simple values (string, number, boolean) and
 * control features that significantly impact application performance
 */
const PERFORMANCE_RELEVANT_FLAGS = [
    "disableMainThreadScheduling",
    "disablePcbLayoutEngineWorker",
    "enablePcb3dReflections",
    "enablePcbFillPartitionAcceleration",
    "performanceMetrics",
];

/**
 * PerformanceMetricsController component
 *
 * This component subscribes to the performanceMetrics feature flag and updates
 * localStorage accordingly when the user is authenticated. It does not modify
 * localStorage when the user is anonymous, preserving the setting when users
 * temporarily log out.
 *
 * Uses Zod parsing to safely handle the feature flag value.
 * Tags Palette with user information and selected performance-relevant feature flag values.
 */
export const PerformanceMetricsController: React.FC = () => {
    const {currentUserService, useFeatureFlagsStore} = useFluxServices();

    const rawPerformanceMetricsValue = useFeatureFlagsStore((state) => state.performanceMetrics);

    useEffect(() => {
        tag("app.version", APP_VERSION);
    }, []);

    useEffect(() => {
        const currentUser = currentUserService.getCurrentUser();

        if (currentUser && !currentUser.isAnonymous) {
            tag("user.uid", currentUser.uid);
            tag("user.handle", currentUser.handle);
        }
    }, [currentUserService]);

    useEffect(() => {
        const allFlags = useFeatureFlagsStore.getState();
        type AllFlagsType = typeof allFlags;

        function isValidFlagKey(key: string): key is keyof AllFlagsType {
            return Object.prototype.hasOwnProperty.call(allFlags, key);
        }

        // eslint-disable-next-line @buildwithflux/no-array-foreach
        PERFORMANCE_RELEVANT_FLAGS.forEach((flagName) => {
            if (isValidFlagKey(flagName)) {
                const value = allFlags[flagName];

                if (
                    value !== undefined &&
                    (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
                ) {
                    tag(`flag.${flagName}`, String(value));
                }
            }
        });

        const unsubscribe = useFeatureFlagsStore.subscribe(() => {
            const updatedFlags = useFeatureFlagsStore.getState();

            // eslint-disable-next-line @buildwithflux/no-array-foreach
            PERFORMANCE_RELEVANT_FLAGS.forEach((flagName) => {
                if (isValidFlagKey(flagName)) {
                    const value = updatedFlags[flagName];

                    if (
                        value !== undefined &&
                        (typeof value === "string" || typeof value === "number" || typeof value === "boolean")
                    ) {
                        tag(`flag.${flagName}`, String(value));
                    }
                }
            });
        });

        return () => {
            unsubscribe();
        };
    }, [useFeatureFlagsStore]);

    useEffect(() => {
        if (rawPerformanceMetricsValue !== undefined) {
            const currentUser = currentUserService.getCurrentUser();

            if (currentUser && !currentUser.isAnonymous) {
                try {
                    const parsedValue = PerformanceMetricsEnum.safeParse(rawPerformanceMetricsValue);
                    const valueToStore = parsedValue.success ? parsedValue.data : DEFAULT_VALUE;

                    localStorage.setItem(PALETTE_ENABLED_KEY, valueToStore);
                } catch (e) {
                    localStorage.setItem(PALETTE_ENABLED_KEY, DEFAULT_VALUE);
                }
            }
        }
    }, [rawPerformanceMetricsValue, currentUserService]);

    return null;
};

export default PerformanceMetricsController;
